import { Alert, Empty, Modal, Tag } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import React, { useMemo, useState } from 'react'
import {
  MalindexBesok,
  MalindexConfig,
  MalindexMal,
  MalindexOmrade,
  MalUppfoljning,
  SparaBesok,
} from '../../../api/malindex/malindexApi'
import { aktivaMal, behovJamforelse, svarForOmrade, tillFlodesSvar, viktandeBehov } from '../malindexCalc'
import { scrollaInIVy } from '../tangentbord'
import FragorMatris from './FragorMatris'
import PrioriteradeOmraden from './PrioriteradeOmraden'
import StegKnappar from './StegKnappar'
import StegRad from './StegRad'
import { arIfyllt, ofullstandigaMal } from './MalRedigerare'
import { MODAL_BREDD, MODAL_GRIND, MODAL_KNAPP, modalTitel } from '../modalStil'
import FordjupningOchMal from './FordjupningOchMal'
import ArendeIdModal from './ArendeIdModal'
import AvbrytModal from './AvbrytModal'
import UppstartModal, { UppstartVarden } from './UppstartModal'

/**
 * Uppföljning – besök 2 och framåt.
 *
 * Uppstarten tas i en modal innan flödet börjar. Sedan upp till fem steg – efter
 * nyttofrågorna frågar en modal om behoven förändrats. Svaret nej avslutar besöket
 * där; ingen ny behovsbild sparas och nästa uppföljning vägs mot samma skattning
 * som denna. Svaret ja öppnar steg 3–5.
 *   1  Målen, ett i taget: uppfyllelse 0–4 + "vill du behålla målet?"
 *   2  Nyttofrågorna, viktigast först. Områden som senast kallades "ej tillämpligt"
 *      hoppas över – de berör inte brukaren och räknas ändå inte in i indexet.
 *   3  Behovsfrågorna, BLANKA. Inga tidigare svar visas här (spec 2.8) – att se
 *      sitt eget förra svar gör att man bekräftar av artighet.
 *   4  Prioriteringen – områdena rangordnade efter de nya svaren.
 *   5  Jämförelsen nu mot förra gången som samtalspunkt, + öppen fråga.
 *   6  Fördjupning och mål sida vid sida (FordjupningOchMal).
 *
 * Prioritering och målformulering följer alltid efter behovsfrågorna, precis som
 * vid kartläggningen – annars kan en förändrad behovsbild inte leda till nya mål.
 *
 * Målen som behållits följer med in i redigeraren, med sina id:n i behåll. Det
 * besök som senast satte mål bär hela den gällande uppsättningen, så ingenting
 * tappas mellan besöken.
 *
 * Metod C (Antons vägval): frågorna ställs alltid och ofärgat, jämförelsen kommer
 * efteråt. Inget index visas någonstans i flödet – det hör hemma efter besöket,
 * utan brukaren i rummet.
 */

export interface UppfoljningProps {
  config: MalindexConfig
  personnummer: string
  /** Brukarens tidigare besök – styr vilka mål som följs upp och vad som jämförs. */
  besok: MalindexBesok[]
  /** Ett pausat besök som återupptas – flödet startar där det slutade. */
  utkast?: MalindexBesok | null
  onSpara: (payload: SparaBesok) => Promise<void>
  onAvbryt: () => void
  sparar?: boolean
}

const STEG = ['Mål', 'Nytta', 'Behovsfrågor', 'Prioritering', 'Jämförelse', 'Fördjupning & mål']

const skalKnapp = (aktiv: boolean) =>
  `min-w-[76px] flex-1 rounded-sm border px-2 py-3 text-center text-sm leading-tight transition-colors ${
    aktiv
      ? 'border-pejlen-500 bg-pejlen-500 text-white font-medium'
      : 'border-gray-200 bg-white text-gray-700 hover:border-pejlen-400 hover:text-pejlen-500'
  }`

const Uppfoljning: React.FC<UppfoljningProps> = ({
  config,
  personnummer,
  besok,
  utkast,
  onSpara,
  onAvbryt,
  sparar,
}) => {
  // Allt initialtillstånd kommer ur utkastet när ett sådant återupptas.
  const utkastSteg = utkast?.utkast_steg ?? 0
  const [steg, setSteg] = useState(utkastSteg)
  const [uppstart, setUppstart] = useState<UppstartVarden | null>(null)
  const [malSvar, setMalSvar] = useState<Record<string, MalUppfoljning>>(() => {
    const ut: Record<string, MalUppfoljning> = {}
    ;(utkast?.mal_uppfoljning ?? []).forEach((u) => {
      ut[u.malId] = u
    })
    return ut
  })
  const [nyttaSvar, setNyttaSvar] = useState<Record<number, number | undefined>>(() =>
    tillFlodesSvar(utkast?.nytta_svar)
  )
  const [behovSvar, setBehovSvar] = useState<Record<number, number | undefined>>(() =>
    tillFlodesSvar(utkast?.behov_svar)
  )
  const [prioriterade, setPrioriterade] = useState<number[]>(utkast?.prioriterade ?? [])
  const [nyaMal, setNyaMal] = useState<MalindexMal[]>(utkast?.mal ?? [])
  // Målsteget seedar bara en gång. Har utkastet redan varit där är seedningen gjord,
  // annars skulle de behållna målen skrivas över det som redan formulerats.
  const [malSeedade, setMalSeedade] = useState(utkastSteg >= 5)
  const [visaArendeId, setVisaArendeId] = useState(false)
  const [visaAvbryt, setVisaAvbryt] = useState(false)
  const [visaBehovsfraga, setVisaBehovsfraga] = useState(false)
  /**
   * Om ny behovsskattning gjordes vid besöket. Nej → besöket avslutas efter nyttan,
   * inget behovssvar sparas, och nästa uppföljning vägs fortsatt mot den tidigare
   * skattningen. Valet behöver ingen egen kolumn: ett besök med nytta men utan behov
   * ÄR ett nej, och går att räkna på i efterhand.
   */
  // Härleds ur steget vid återupptagande: steg 2 och framåt nås bara genom ett ja.
  const [behovStalldes, setBehovStalldes] = useState(utkastSteg >= 2)
  const [note, setNote] = useState(utkast?.note ?? '')

  const mal = useMemo(() => aktivaMal(besok), [besok])
  const viktande = useMemo(() => viktandeBehov(besok), [besok])
  const maxPrio = config.maxPrioriterade

  const togglePrio = (id: number) =>
    setPrioriterade((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length < maxPrio ? [...p, id] : p))

  const valjBehov = (id: number, varde: number) => {
    setBehovSvar((s) => ({ ...s, [id]: varde }))
    // Ett område som markerats "ej tillämpligt" kan inte vara prioriterat.
    if (varde === config.ejTillampligt.varde) setPrioriterade((p) => p.filter((x) => x !== id))
  }

  const prioOmraden: MalindexOmrade[] = useMemo(
    () => config.omraden.filter((o) => prioriterade.includes(o.id)),
    [config.omraden, prioriterade]
  )

  /** Målen som lever vidare efter besökets behåll-svar. */
  const kvarvarandeMal = mal.filter((m) => malSvar[m.malId]?.behall !== false)

  /** Målformuleringen är obligatorisk – påbörjade mål utan den låser sparandet. */
  const halvfardigaMal = ofullstandigaMal(nyaMal)

  /**
   * Seedar målredigeraren en gång, med de mål brukaren svarat att hen vill behålla.
   * De behåller sina malId så att historiken hänger ihop över besöken.
   */
  const oppnaMalsteget = () => {
    if (!malSeedade) {
      setNyaMal(kvarvarandeMal)
      setMalSeedade(true)
    }
    setSteg(5)
  }

  const sattMal = (malId: string, delta: Partial<MalUppfoljning>) =>
    setMalSvar((s) => ({
      ...s,
      [malId]: { uppfyllelse: null, behall: null, ...s[malId], ...delta, malId },
    }))

  /**
   * Behåll-frågan är tvingande. Ett obesvarat mål gick tidigare igenom som null, och
   * då kunde varken systemet eller nästa handläggare avgöra om målet fortfarande
   * gällde – "vet ej" är inget svar när det avgör vad som ska följas upp härnäst.
   */
  const obesvaradeMal = mal.filter((m) => malSvar[m.malId]?.behall === undefined || malSvar[m.malId]?.behall === null)

  /**
   * Områdena som nyttofrågorna faktiskt ställs om, i den ordning de betyder mest.
   *
   * FILTRET: ett område som brukaren senast kallade "ej tillämpligt" berör hen inte,
   * och då är "har hemtjänsten hjälpt dig med det?" en fråga utan innehåll. Området är
   * redan uteslutet ur indexet, så det kostar ingenting att hoppa över det.
   *
   * ORDNINGEN: viktigast först. Indexet viktas av behovet, så det är de högt skattade
   * områdena som avgör talet – de ska ställas medan brukaren är som piggast.
   *
   * Styrande är den VIKTANDE skattningen, alltså den senaste som fanns före besöket.
   * Det är den nyttan vägs mot, så det är den som ska bestämma vad som frågas.
   * Behovsfrågorna i steg 3 ställs fortfarande om alla tio – brukaren kan mycket väl
   * ha ändrat sig om vad som berör hen, och det är just det vi vill fånga.
   */
  const nyttoOmraden = useMemo(() => {
    const behovFor = (o: MalindexOmrade) => svarForOmrade(viktande?.behov_svar, o.id)
    return config.omraden
      .map((omrade, ordning) => ({ omrade, ordning, behov: behovFor(omrade) }))
      .filter((x) => x.behov !== config.ejTillampligt.varde)
      // Obesvarade områden sist. -1 håller dem under 0, som är ett giltigt svar.
      .sort((a, b) => (b.behov ?? -1) - (a.behov ?? -1) || a.ordning - b.ordning)
      .map((x) => x.omrade)
  }, [config.omraden, config.ejTillampligt.varde, viktande])

  /**
   * Båda matriserna måste fyllas i helt innan steget kan lämnas. "Ej tillämpligt" gör
   * att det alltid går att svara, så ett tomt fält betyder att området missades.
   */
  const allNytta = nyttoOmraden.every((o) => nyttaSvar[o.id] !== undefined)
  const allBehov = config.omraden.every((o) => behovSvar[o.id] !== undefined)

  const jamforelse = useMemo(() => {
    const nu: Record<string, number> = {}
    config.omraden.forEach((o) => {
      const v = behovSvar[o.id]
      if (v !== undefined) nu[String(o.id)] = v
    })
    return behovJamforelse(config.omraden, nu, viktande)
  }, [config.omraden, behovSvar, viktande])

  /**
   * Samma nyttolast oavsett om besöket sparas eller pausas – skillnaden är `draft`.
   *
   * Ett utkast sparar allt som fyllts i så långt, även behovssvar som ännu inte är
   * kompletta. Först vid ett riktigt sparande gäller reglerna om vad som får saknas.
   */
  const byggPayload = (u: UppstartVarden, arendeId: string | null, draft: boolean): SparaBesok => {
    const plocka = (kalla: Record<number, number | undefined>) => {
      const ut: Record<string, number> = {}
      config.omraden.forEach((o) => {
        const v = kalla[o.id]
        if (v !== undefined) ut[String(o.id)] = v
      })
      return ut
    }
    const trimmad = note.trim()

    return {
      id: utkast?.id ?? null,
      pnr: '',
      pid: arendeId || null,
      draft,
      utkastSteg: draft ? steg : null,
      // Vad handläggaren valde att starta, inte något som härleds efteråt.
      besokstyp: 'uppfoljning',
      kanal: u.kanal,
      svarande: u.svarande,
      demensdiagnos: u.demensdiagnos,
      nyttaSvar: plocka(nyttaSvar),
      // Avslutades besöket efter nyttan sparas ingen behovsbild alls – null, inte ett
      // tomt objekt, så att "ingen ny skattning" inte går att förväxla med "obesvarad".
      // I ett utkast sparas däremot allt som fyllts i, oavsett hur långt man kommit.
      behovSvar: draft || behovStalldes ? plocka(behovSvar) : null,
      prioriterade: draft || behovStalldes ? prioriterade : [],
      // Hela den gällande måluppsättningen efter besöket: behållna mål plus nya.
      // Tom när besöket avslutades tidigt – då gäller föregående uppsättning vidare,
      // minus de mål som fått nej på behåll-frågan.
      // Helt tomma rader kastas. Halvfyllda kommer aldrig hit – knappen är låst då.
      mal: draft ? nyaMal : behovStalldes ? nyaMal.filter(arIfyllt) : [],
      malUppfoljning: mal.map((m) => malSvar[m.malId] ?? { malId: m.malId, uppfyllelse: null, behall: null }),
      note: trimmad.length > 0 ? trimmad : undefined,
    }
  }

  const handleSpara = (arendeId: string | null) => {
    if (sparar || !uppstart) return
    onSpara(byggPayload(uppstart, arendeId, false))
  }

  /** Pausar besöket. Inget ÄrendeID efterfrågas – det hör till ett färdigt besök. */
  const handlePausa = () => {
    if (sparar || !uppstart) return
    onSpara(byggPayload(uppstart, null, true))
  }

  // Modalen är grinden: flödet renderas först när uppstarten är gjord.
  if (!uppstart) {
    return (
      <UppstartModal
        config={config}
        open
        besokstyp="uppfoljning"
        personnummer={personnummer}
        aterupptar={!!utkast}
        tidigareDemensdiagnos={utkast?.demensdiagnos}
        onKlar={setUppstart}
        onAvbryt={onAvbryt}
      />
    )
  }

  return (
    // Flödet fyller skärmen så att knappraden kan tryckas ned till botten.
    <div className="flex flex-col" style={{ minHeight: 'calc(100vh - 96px)' }}>
      <StegRad steg={STEG} aktivt={steg} />

      {steg === 0 && (
        <div className="flex flex-1 flex-col gap-4">
          {mal.length === 0 ? (
            // Bara text, ingen figur. Det här är inte en tom lista utan ett normalt
            // läge i metoden – ett besök behöver inte mynna ut i mål – och en
            // illustration gör det till ett undantag som ser ut att ha gått fel.
            <p className="max-w-3xl text-base leading-snug text-gray-700">
              Under föregående besök sattes inga mål och därför finns inga mål att följa upp. Gå vidare till
              nyttofrågorna.
            </p>
          ) : (
            mal.map((m, i) => {
              const svar = malSvar[m.malId]
              return (
                <div key={m.malId} className="max-w-3xl rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <Tag color="blue">Mål {i + 1}</Tag>

                  {/*
                    Målformuleringen först, i klartext. Det är den som gäller, och den
                    som ska gå att läsa av.

                    Brukarens egna ord står under, i samma storlek och färg men i
                    citatform – det är den meningen man läser upp ordagrant, för det är
                    den hon känner igen. Två uttryck för samma sak, jämbördiga.
                  */}
                  {/* `leading-snug` på båda: radhöjden, inte marginalen, är det som
                      skapar glappet mellan två textrader. 1,5 ger ~4px tomrum över och
                      under bokstäverna i varje rad. */}
                  <p className="mt-2 text-base leading-snug text-gray-800">{m.maltext.trim() || m.egnaOrd.trim()}</p>
                  {m.maltext.trim() && m.egnaOrd.trim() && (
                    <p className="mt-0.5 text-base italic leading-snug text-gray-800">”{m.egnaOrd.trim()}”</p>
                  )}

                  {/* Luft mellan grupperna: målet, uppfyllelsen och behåll-frågan är tre
                      skilda saker och ska inte läsas som ett block. */}
                  <p className="mb-2 mt-6 text-base font-medium text-gray-800">{config.mal.uppfyllelseFraga}</p>
                  <div className="flex flex-wrap gap-2">
                    {config.skala.map((alt) => (
                      <button
                        key={alt.varde}
                        type="button"
                        aria-pressed={svar?.uppfyllelse === alt.varde}
                        onClick={() => sattMal(m.malId, { uppfyllelse: alt.varde })}
                        className={skalKnapp(svar?.uppfyllelse === alt.varde)}
                      >
                        {alt.etikett}
                      </button>
                    ))}
                  </div>

                  <p className="mb-2 mt-6 text-base font-medium text-gray-800">{config.mal.behallFraga}</p>
                  <div className="flex gap-2">
                    {[
                      { etikett: 'Ja', v: true },
                      { etikett: 'Nej', v: false },
                    ].map((k) => (
                      <button
                        key={k.etikett}
                        type="button"
                        aria-pressed={svar?.behall === k.v}
                        onClick={() => sattMal(m.malId, { behall: k.v })}
                        className={`${skalKnapp(svar?.behall === k.v)} max-w-[110px]`}
                      >
                        {k.etikett}
                      </button>
                    ))}
                  </div>
                </div>
              )
            })
          )}

          <StegKnappar
            knappar={[
              {
                etikett: 'Nästa',
                onClick: () => setSteg(1),
                variant: 'primar',
                disabled: obesvaradeMal.length > 0,
                title:
                  obesvaradeMal.length > 0
                    ? `Ta ställning till om ${obesvaradeMal.length === 1 ? 'målet' : 'alla mål'} ska behållas`
                    : '',
              },
              { etikett: 'Avbryt besök', onClick: () => setVisaAvbryt(true) },
            ]}
          />
        </div>
      )}

      {steg === 1 && (
        <div className="flex flex-1 flex-col gap-4">
          {nyttoOmraden.length === 0 ? (
            // Nås bara om varenda område kallades ej tillämpligt vid förra skattningen.
            // Osannolikt, men inte omöjligt sedan skattningen blev tvingande.
            <p className="max-w-3xl text-base leading-snug text-gray-700">
              Alla områden var markerade som ej tillämpliga vid förra behovsskattningen, så det finns inga
              nyttofrågor att ställa. Gå vidare för att ta ställning till om behoven förändrats.
            </p>
          ) : (
            <FragorMatris
              leadIn={config.nyttoLeadIn}
              omraden={nyttoOmraden}
              skala={config.skala}
              ejTillampligt={config.ejTillampligt}
              variant="nytta"
              svar={nyttaSvar}
              onValj={(id, v) => setNyttaSvar((s) => ({ ...s, [id]: v }))}
            />
          )}

          {!viktande && (
            <Alert
              type="warning"
              showIcon
              className="max-w-3xl"
              message="Ingen tidigare behovsskattning"
              description="Det saknas en behovsskattning att vikta nyttan mot, så inget index kan räknas ut för det här besöket. Svaren sparas ändå."
            />
          )}

          {/* Grinden: har behoven ändrats avgör om resten av besöket alls ska göras. */}
          <StegKnappar
            knappar={[
              { etikett: 'Tillbaka', onClick: () => setSteg(0) },
              {
                etikett: 'Nästa',
                onClick: () => setVisaBehovsfraga(true),
                variant: 'primar',
                disabled: !allNytta,
                title: allNytta ? '' : 'Alla områden måste besvaras',
              },
              { etikett: 'Avbryt besök', onClick: () => setVisaAvbryt(true) },
            ]}
          />
        </div>
      )}

      {steg === 2 && (
        <div className="flex flex-1 flex-col gap-4">
          {/* Inga tidigare svar visas här. Jämförelsen kommer i nästa steg, efter svaren. */}
          <FragorMatris
            leadIn={config.behovLeadInUppfoljning}
            omraden={config.omraden}
            skala={config.skala}
            ejTillampligt={config.ejTillampligt}
            variant="behov"
            svar={behovSvar}
            onValj={valjBehov}
          />

          <StegKnappar
            knappar={[
              { etikett: 'Tillbaka', onClick: () => setSteg(1) },
              {
                etikett: 'Nästa',
                onClick: () => setSteg(3),
                variant: 'primar',
                disabled: !allBehov,
                title: allBehov ? '' : 'Alla områden måste besvaras',
              },
              { etikett: 'Avbryt besök', onClick: () => setVisaAvbryt(true) },
            ]}
          />
        </div>
      )}

      {steg === 3 && (
        <div className="flex flex-1 flex-col">
          <PrioriteradeOmraden config={config} svar={behovSvar} prioriterade={prioriterade} onToggle={togglePrio} />

          <StegKnappar
            knappar={[
              { etikett: 'Tillbaka', onClick: () => setSteg(2) },
              {
                etikett: 'Nästa',
                onClick: () => setSteg(4),
                variant: 'primar',
                disabled: prioriterade.length === 0,
                title: prioriterade.length === 0 ? 'Markera minst ett prioriterat område' : '',
              },
              { etikett: 'Avbryt besök', onClick: () => setVisaAvbryt(true) },
            ]}
          />
        </div>
      )}

      {steg === 4 && (
        <div className="flex flex-1 flex-col gap-5">
          <div>
            <p className="text-lg font-semibold text-gray-800">Nu jämfört med förra gången</p>
            <p className="mb-3 max-w-2xl text-base text-gray-500">
              Visas först nu, efter att frågorna besvarats — så att svaren inte påverkas av vad som sades sist.
              Använd den som samtalspunkt: <span className="italic">”vill du säga något om det?”</span>
            </p>

            {!viktande ? (
              <Empty description="Ingen tidigare behovsskattning att jämföra mot." />
            ) : (
              <div className="max-w-3xl overflow-x-auto">
                <table className="w-full text-base">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-sm text-gray-500">
                      <th className="py-1.5 pr-3 font-medium">Område</th>
                      <th className="py-1.5 pr-3 font-medium">Nu</th>
                      <th className="py-1.5 pr-3 font-medium">Förra gången</th>
                      <th className="py-1.5 font-medium">Förändring</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jamforelse.map((r) => (
                      <tr key={r.omradeId} className="border-b border-gray-100">
                        <td className="py-1.5 pr-3 text-gray-800">{r.namn}</td>
                        <td className="py-1.5 pr-3 text-gray-700" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {r.nu ?? '–'}
                        </td>
                        <td className="py-1.5 pr-3 text-gray-500" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {r.forra ?? '–'}
                        </td>
                        <td className="py-1.5">
                          {r.forandring === null || r.forandring === 0 ? (
                            <span className="text-gray-400">oförändrat</span>
                          ) : (
                            <Tag color={r.forandring > 0 ? 'green' : 'orange'}>
                              {r.forandring > 0 ? '+' : ''}
                              {r.forandring}
                            </Tag>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-4 max-w-3xl">
            <label className="mb-2 block text-lg font-medium text-gray-800">{config.oppenFraga}</label>
            <TextArea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anteckna det brukaren tar upp…"
              autoSize={{ minRows: 3, maxRows: 6 }}
              onFocus={scrollaInIVy}
              style={{ resize: 'none', fontSize: 16 }}
            />
          </div>

          <StegKnappar
            knappar={[
              { etikett: 'Tillbaka', onClick: () => setSteg(3) },
              { etikett: 'Nästa', onClick: oppnaMalsteget, variant: 'primar' },
              { etikett: 'Avbryt besök', onClick: () => setVisaAvbryt(true) },
            ]}
          />
        </div>
      )}

      {steg === 5 && (
        <>
          <FordjupningOchMal
            config={config}
            prioOmraden={prioOmraden}
            mal={nyaMal}
            onAndraMal={setNyaMal}
            note={note}
            onAndraNote={setNote}
          />
          <StegKnappar
            knappar={[
              { etikett: 'Tillbaka', onClick: () => setSteg(4) },
              {
                etikett: sparar ? 'Sparar…' : 'Spara besök',
                onClick: () => setVisaArendeId(true),
                variant: 'primar',
                disabled: sparar || halvfardigaMal.length > 0,
                title: halvfardigaMal.length > 0 ? 'Formulera målet, eller ta bort det' : '',
              },
              { etikett: 'Avbryt besök', onClick: () => setVisaAvbryt(true) },
            ]}
          />
        </>
      )}

      {/* Vägvalet efter nyttofrågorna. Nej avslutar besöket direkt – då finns inget mer
          att fråga om, och nästa uppföljning vägs mot samma behovsbild som denna. */}
      <Modal
        open={visaBehovsfraga}
        {...MODAL_GRIND}
        title={modalTitel('Har behoven förändrats?')}
        okText="Ja, ställ behovsfrågorna"
        okButtonProps={MODAL_KNAPP}
        onOk={() => {
          setBehovStalldes(true)
          setVisaBehovsfraga(false)
          setSteg(2)
        }}
        cancelText="Nej, avsluta besök och spara"
        cancelButtonProps={MODAL_KNAPP}
        onCancel={() => {
          setBehovStalldes(false)
          setVisaBehovsfraga(false)
          setVisaArendeId(true)
        }}
        width={MODAL_BREDD}
      >
        {/*
          Rekommendationen, inte en genväg. Målformuleringen ligger sist i behovsspåret
          därför att målet ska bygga på den prioritering brukaren nyss gjort – att nå
          målsteget utan den vore att formulera i luften. Står brukaren utan mål är
          alltså ja-vägen den enda som leder till ett nytt, och det ska sägas här och
          inte upptäckas efteråt.
        */}
        {kvarvarandeMal.length === 0 && (
          <Alert
            type="warning"
            showIcon
            // Inline, inte `mb-7`. Tailwinds marginalklasser biter inte på Ant Designs
            // komponenter här – samma sak har hänt med kolumnrubrikernas justering och
            // med processlinjens marginal. Ant Designs stilmall laddas efter Tailwinds
            // utilities, så vid samma specificitet vinner Ant.
            style={{ marginBottom: 32 }}
            message="Inga mål kvar efter besöket"
            // Ant Designs beskrivningstext är 14px i en egen, mörkare färg. Resten av
            // modalen är 16px text-gray-700, och rutan ska läsas som samma röst – det
            // är samma beslut den handlar om.
            description={
              <span className="text-base text-gray-700">
                Svarar du nej står brukaren utan mål till nästa besök. Ett nytt mål kan bara sättas via
                behovsfrågorna.
              </span>
            }
          />
        )}

        {/*
          Uppslagsbok, inte prosa. Två stycken som båda började med "Svarar du…" lästes
          som en text man måste ta i sin helhet; med svaret först letar man upp det
          alternativ man funderar på och läser bara den raden.
        */}
        <p className="m-0 text-base text-gray-700">
          <span className="font-medium">Ja</span> — ni går vidare till behovsfrågorna, jämförelsen mot förra gången
          och målformuleringen.
        </p>
        <p className="mt-2 mb-0 text-base text-gray-700">
          <span className="font-medium">Nej</span> — besöket avslutas här. Ingen ny behovsbild sparas, och nästa
          uppföljning vägs mot samma behovsskattning som den här.
        </p>
      </Modal>

      <AvbrytModal
        open={visaAvbryt}
        personnummer={personnummer}
        sparar={sparar}
        onPausa={handlePausa}
        onKasta={onAvbryt}
        onFortsatt={() => setVisaAvbryt(false)}
      />

      <ArendeIdModal
        open={visaArendeId}
        sparar={sparar}
        onBekrafta={handleSpara}
        onAvbryt={() => setVisaArendeId(false)}
      />
    </div>
  )
}

export default Uppfoljning
